// 도로 «등급» 사전 굽기 로더 — `.road.bin` 을 읽어 `roadKindAt` 의 답을 대신 낸다.
//
// 계획 정본: `docs/plan/2026-08-29/1038_지형_사전굽기_롤백기준과_tech시뮬.md` §단계 1 ㉰
// 굽는 쪽:   `front/scripts/bake_road_grade.mjs`
//
// ══════════════════════════════════════════════════════════════════════════════
// [무엇을 없애나] `roadCells.roadKindAt` 은 **모든 지형 컬럼**이 부르는 자리다. 부를 때마다
//   32m 버킷을 열어 그 안의 도로 폴리곤에 점-다각형 판정을 돌린다. 굽기가 없애려는 CPU 가
//   전부 여기 있다 — 구운 타일이 있으면 **바이트 배열 조회 한 번**으로 끝난다.
//
// ══════════════════════════════════════════════════════════════════════════════
// [★ 이 모듈은 «정수 격자 위에서만» 답한다 — 이게 안전의 근거다]
// ══════════════════════════════════════════════════════════════════════════════
// 구운 값은 정수 월드 좌표 (x0+dx, z0+dz) 에서 `roadKindAt` 을 부른 답 그 자체다
// (굽기 스크립트가 런타임과 **같은 함수**를 부른다 — `roadMaskFill`. 감사 30,000칸 불일치 0).
// 그래서 **정수점에서는 비트 단위로 같은 답**이고, 정수가 아닌 점은 애초에 안 답한다(-1).
//
// ⚠ 왜 소수점을 안 답하나 — `onRoad` 는 건축·굴착 금지 판정에 쓰이고, 서버
//   (`estate/service.py on_road`)가 **폴리곤 정밀도**로 막는다. 구운 값은 1m 격자라
//   `Math.floor` 로 답하면 길 가장자리에서 최대 1m 어긋나고, 그 어긋남이 곧
//   「고스트는 초록인데 서버가 거절」이다. 지형 컬럼(`buildColumn`)과 LOD(`roadLod`)는
//   원래 정수 격자만 묻는다 — 빨라져야 하는 자리는 전부 정수 쪽이라 잃는 게 없다.
//
// ══════════════════════════════════════════════════════════════════════════════
// [굽기가 깨지면 — 엄격 모드(기본)는 계산으로 대신하지 않는다]
// ══════════════════════════════════════════════════════════════════════════════
// 매니페스트 없음 · 지문 불일치 · 타일 404/길이 불일치 → `bakeStrict.bakeMissing` 으로 신고하고
// `roadBakeFill` 은 -2 를 준다 — `roadMaskFill` 은 폴리곤 판정 없이 «도로 없음»으로 둔다.
// 타일이 «아직 안 온» 것(-1)만 종전 폴리곤 경로로 흐른다. 점 조회 `roadBakeKindAt` 은 건축
// 판정 정밀도 때문에 여전히 -1 → 폴리곤이다(한 점이라 싸다).
// `?lodstrict=0` 이면 옛 동작(저하 신고 + 폴리곤 폴백). `?roadbake=0` 은 굽기 자체를 끈다.
//
// ⚠ **타일 URL 에 지문을 싣는다**(`?v=<rev>`). 서비스워커가 `.bin` 을 cache-first 로 물고
//   (`front/public/sw.js` v30), 그건 브라우저 HTTP 캐시를 못 지운다. 지문이 URL 에 없으면
//   다시 구워도 옆 사람은 영영 옛 파일을 받는다 — 그 증상은 「배포 실패」처럼 보인다.
import { stickyBool, stickyBudget } from '@engine/debugFlags';
import { noteSet } from '@engine/memBudget';
import { noteDegrade } from '@engine/degradeRegistry';
import { BAKE_STRICT, bakeMissing } from '@engine/bakeStrict';

const KEY = 'seoul.global';
const HF_TILE = 128;          // ⚠ heightfieldData.HF_TILE 과 **같은 값**이어야 한다

/** `?roadbake=0` — 사전 굽기를 끄고 종전 폴리곤 경로로 되돌린다(§롤백). */
const ON = stickyBool('roadbake', true);
/** `?roadbaketiles=N` — 상주 타일 수. 한 장이 1MB(1,024²×1B)라 기본 9장 = 9MB.
 *  ⚠ 하이트필드 타일 상주가 96장×32KiB ≈ 3MiB 인 것과 견주면 큰 편이다. 안 온 타일은
 *    폴리곤으로 떨어질 뿐이라 **줄여도 그림이 안 깨진다** — 느려질 뿐이다. */
const TILE_MAX = stickyBudget('roadbaketiles', 9);
const PAR = 4;                // 동시 fetch 상한 — 하이트필드 타일(6)과 대역을 나눠 쓴다

let _mf = null;               // null | 'loading' | 'fail' | 매니페스트 객체
let _ok = null;               // null=미검증 · true=지문 맞음 · false=안 씀(영구)
let _have = null;             // Set<"tx_tz"> — 매니페스트에 실린 타일(404 를 안 부르려고)
let _tiles = new Map();       // "tx_tz" → Uint8Array | 'loading' | 'missing'
let _inflight = 0;
const _queue = [];
let _anchor = null;           // [tx, tz] — 이 둘레만 받고, 축출도 여기서 먼 것부터
let _epoch = 0;               // 구운 타일이 «도착»한 횟수 — roadCells 가 무효화 판정에 쓴다
let _onLand = null;           // 타일 도착 통지(roadCells 가 건다)

// 기하 — 매니페스트가 오기 전엔 아무것도 못 한다(전부 -1 로 떨어진다)
let OX = 0, OZ = 0, TILE_M = 0, N = 0, REV = '';

// ── 마지막 타일 기억 — ★ 여기가 성능의 절반이다 ──────────────────────────────
// `roadKindAt` 은 지형 컬럼마다 불린다. 칸마다 `Map.get()` 에 넣을 키 문자열을 새로 만들면
// **문자열을 칸 수만큼 새로 만든다** — `roadMaskFill` 이 창 하나 51ms 를 먹던 바로 그 이유다
// (roadCells.js 머리주석). 연속한 조회는 거의 같은 타일이므로 마지막 한 장만 기억한다.
//
// ⚠ ★ **「없음」도 기억되므로 세대(`_gen`)가 있어야 한다**.
//   처음엔 좌표만 비교했다. 그랬더니 타일이 «오기 전»에 한 번 물어본 자리가 `null` 로
//   기억되고, 그 뒤 타일이 도착해도 좌표가 같으니 **그 기억을 영영 다시 쓴다** — 조회가
//   계속 -1 을 주고, 호출자는 얌전히 폴리곤으로 떨어진다. 즉 **그림은 멀쩡한데 굽기가
//   통째로 죽는다.** 화면으로는 절대 안 잡히는 종류의 버그다(그래서 캡처가 아니라
//   `front/scripts/test_road_bake_loader.mjs` 가 필요했다).
//   `_tiles` 가 바뀔 때마다 `_gen` 을 올리고, 기억은 같은 세대에서만 유효하다.
let _lx = NaN, _lz = NaN, _lbuf = null, _lgen = -1;
let _gen = 0;                 // `_tiles` 가 바뀐 횟수 — 위 기억의 유효 세대

const tkey = (tx, tz) => `${tx}_${tz}`;
const ROAD_FIX = 'node front/scripts/bake_terrain_all.mjs --only road';

/** 굽기가 깨졌을 때의 신고 — 엄격 모드면 `bakeMissing`(에러·토스트·서버), 아니면 옛 저하 신고. */
const reportBroken = (id, what) => {
  if (BAKE_STRICT) bakeMissing(id, what, ROAD_FIX);
  else noteDegrade(id, { what: `${what} — 칸마다 폴리곤을 다시 판정합니다`, cost: '창 하나 51ms · 실기 65장 3,303ms', fix: ROAD_FIX });
};

/** 이 모듈이 지금 답을 낼 수 있나 — 스위치·지문이 모두 통과했을 때만 참. */
export const roadBakeReady = () => ON && _ok === true;
/** 구운 타일이 «도착한» 횟수. 값이 바뀌면 그 자리 컬럼은 「길 없음」으로 굳어 있을 수 있다. */
export const roadBakeEpoch = () => _epoch;
/** 진단용 — 콘솔에서 「지금 몇 장 물고 있나」. */
export const roadBakeStats = () => ({
  on: ON, ok: _ok, rev: REV, tiles: _tiles.size, have: _have ? _have.size : 0,
  resident: [..._tiles.values()].filter((v) => typeof v !== 'string').length,
});
/** 타일이 도착하면 부른다 — roadCells 가 무효화 도장을 올리려고 건다.
 *  @param {(rect:{x0:number,z0:number,x1:number,z1:number})=>void} fn 도착한 타일의 «월드 사각형»을 받는다. */
export const onRoadBakeLand = (fn) => { _onLand = fn; };

let _meta = null;

// 지문 대조 — 하나라도 갈리면 **영구히 안 쓴다**. 되살리려면 다시 구워야 한다.
// 도로 타일은 도로 도형만 칠한 것이라 높이값(h_src)과 무관하다 — 타일 번호를 정하는 격자 모양만 본다.
const verify = () => {
  if (_ok !== null || !_meta || !_mf || typeof _mf === 'string') return;
  const j = _mf;
  const bad = [];
  if (j.origin_x !== _meta.origin_x || j.origin_z !== _meta.origin_z) bad.push('원점');
  if (j.cell !== _meta.cell) bad.push('셀');
  if (j.hf_w !== _meta.w || j.hf_h !== _meta.h) bad.push('격자크기');
  if (j.bytes_per_cell !== 1 || j.grid_m !== 1) bad.push('포맷');
  if (j.tile_m !== HF_TILE * _meta.cell) bad.push('타일크기');
  _ok = bad.length === 0;
  if (!_ok) {
    reportBroken('bake:road:sig', `구운 도로 지문 불일치(${bad.join('·')}) — 굽기 h_src=${j.h_src} / 지금 ${_meta.h_src}`);
  }
};

// ── 매니페스트 ───────────────────────────────────────────────────────────────
const loadManifest = () => {
  if (!ON || _mf) return;
  _mf = 'loading';
  fetch(`/terrain_baked/${KEY}.road.json`)
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      if (!j || j.format !== 'road-grade-v1') {
        _mf = 'fail'; _ok = false;
        reportBroken('bake:road:manifest', `구운 도로 매니페스트가 없거나 형식이 다릅니다(/terrain_baked/${KEY}.road.json)`);
        return;
      }
      _mf = j;
      _have = new Set((j.tiles || []).map((t) => String(t.tile).replace(/^t/, '')));
      OX = j.origin_x; OZ = j.origin_z; TILE_M = j.tile_m; N = j.n; REV = String(j.rev || '');
      verify();
      if (_ok && _anchor) { const a = _anchor; _anchor = null; setRoadBakeAnchor(a[0], a[1]); }
    })
    .catch(() => {
      _mf = 'fail'; _ok = false;
      reportBroken('bake:road:fetch', `구운 도로 매니페스트를 못 받았습니다(/terrain_baked/${KEY}.road.json)`);
    });
};

/** 런타임 하이트필드 메타를 알려준다 — roadCells.setRoadGrid 가 이미 받는 그 meta.
 *  ⚠ 이 모듈은 heightfieldData 를 **import 하지 않는다**(그쪽이 roadCells 를 import 하므로
 *    순환이 된다). 그래서 값을 «받아» 쓴다. */
export const setRoadBakeMeta = (meta) => {
  if (!ON || !meta) return;
  _meta = meta;
  loadManifest();
  verify();
};

// ── 타일 받기 ────────────────────────────────────────────────────────────────
const noteMem = () => {
  let bytes = 0;
  for (const v of _tiles.values()) if (typeof v !== 'string') bytes += v.length;
  noteSet('road.baked', bytes);
};

const pump = () => {
  while (_inflight < PAR && _queue.length) {
    const [tx, tz] = _queue.shift();
    const k = tkey(tx, tz);
    if (_tiles.get(k) !== 'loading') continue;      // 그새 취소·축출됐다
    _inflight += 1;
    const url = `/terrain_baked/tiles/${KEY}/t${tx}_${tz}.road.bin`;
    const fail = (why) => { _tiles.set(k, 'missing'); _gen += 1; reportBroken(`bake:road:t${k}`, `구운 도로 타일 t${k} — ${why}`); };
    fetch(`${url}?v=${REV}`)
      .then(async (r) => {
        if (!r.ok) { fail(`${url} HTTP ${r.status}`); return; }
        const buf = new Uint8Array(await r.arrayBuffer());
        // 길이가 안 맞으면 **버린다** — 반쯤 맞는 도로는 안 맞는 도로보다 나쁘다.
        if (buf.length !== N * N) { fail(`길이 ${buf.length} ≠ ${N * N} (낡은 굽기)`); return; }
        _tiles.set(k, buf);
        _gen += 1;
        _epoch += 1;
        // ★ 「어느 «자리»가 왔나」를 같이 준다. 종전엔 인자 없이 불렀고,
        //   받는 쪽(`roadCells` → 두 지형 밭)은 「어딘가 바뀌었다」밖에 몰라 **밭을 통째로
        //   버렸다**. 타일 하나가 온 것뿐인데 반경 1km 컬럼 1,200개가 날아갔다.
        //   이 사각형이 그 폐기를 그 타일로 좁힌다.
        if (_onLand) {
          // ★ 2026-08-30 2차 — 타일«전체»가 아니라 **길이 «있는» 자리만** 낸다.
          //   1차는 타일 상자 하나(1,024m)를 냈고, 받는 LOD 밭은 그 안의 컬럼을 통째로
          //   버렸다 — 실측으로 **한 번에 컬럼 278개**, 그때 fps 가 14~19 로 처박혔다
          //   (`docs/verify/2026-08-30/pcwalk2/walk.json` · 47·61·75초 세 번 다 이 사건).
          //   그런데 타일 안에서 **도로 칸은 0.1~6.7% 뿐**이다(구운 43장 중 6장 실측).
          //   나머지는 길이 없으므로 「길 없음」으로 만들어 둔 컬럼이 **이미 맞다** — 버릴 이유가 없다.
          const rects = roadRectsOf(buf, tx, tz);
          try { if (rects === null || rects.length) _onLand(rects); } catch { /* 구독자 실패는 무시 */ }
        }
      })
      .catch((e) => fail(`받기 실패: ${String(e?.message || e).slice(0, 80)}`))
      .finally(() => { _inflight -= 1; noteMem(); pump(); });
  }
};

// ── ★ 「이 타일 안에서 «어디에» 길이 있나」 ──────────────────────
//
// [무엇을 고치나] 구운 타일이 도착하면 그 자리 지형 컬럼은 낡았을 수 있다 — 링이 아직
//   안 와서 「길 없음」으로 굳은 컬럼이 있기 때문이다. 그런데 그건 **길이 있는 자리**
//   얘기다. 길이 없는 자리의 컬럼은 「길 없음」이 정답이라 다시 만들 이유가 없다.
//
// [고른 눈금 — 64m] 받는 쪽 LOD 컬럼이 `SUB(16) × SCALE(4)` = **64m** 다. 그보다 잘게
//   내도 어차피 컬럼 단위로 뭉쳐지므로 이득이 없고, 사각형만 늘어 `dropRects` 가 비싸진다.
//   실측(구운 타일 6장): 도로 칸 0.1~6.7% → 64m 블록 점유 **7.8~72.7%**, 병합 사각형 **8~34개**.
//   즉 흔한 타일에서 폐기 범위가 **1/2.6**, 한산한 타일에서는 **1/13** 로 준다.
//
// [병합] 가로 런을 만들고, 같은 폭이 이어지는 만큼 세로로 늘린다(그리디 메싱과 같은 수법).
//   상한을 넘기면 **`null`**(=「모른다」)을 준다 → 받는 쪽이 종전대로 타일 전체를 버린다.
//   안전측이 기본이다.
const ROAD_BLOCK_M = 64;
const ROAD_RECT_MAX = 64;

/** 구운 타일 마스크 → 「길이 있는 자리」 월드 사각형 목록.
 *  @returns {Array<{x0,z0,x1,z1}>|null} 빈 배열 = 이 타일엔 길이 없다(버릴 것 없음)
 *                                       · `null` = 못 가렸다 → 타일 전체(호출자가 안전측으로)
 */
const roadRectsOf = (buf, tx, tz) => {
  const B = ROAD_BLOCK_M, G = Math.floor(N / B);
  if (!G || G * B !== N) return null;                 // 눈금이 안 떨어진다 — 모른다
  const occ = new Uint8Array(G * G);
  for (let z = 0; z < N; z += 1) {
    const gz = ((z / B) | 0) * G, row = z * N;
    for (let x = 0; x < N; x += 1) {
      if (buf[row + x]) occ[gz + ((x / B) | 0)] = 1;
    }
  }
  const used = new Uint8Array(G * G);
  const ox = OX + tx * TILE_M, oz = OZ + tz * TILE_M;
  const out = [];
  for (let gz = 0; gz < G; gz += 1) {
    for (let gx = 0; gx < G; gx += 1) {
      const i = gz * G + gx;
      if (!occ[i] || used[i]) continue;
      let x1 = gx;                                    // 가로 런
      while (x1 + 1 < G && occ[gz * G + x1 + 1] && !used[gz * G + x1 + 1]) x1 += 1;
      let z1 = gz;                                    // 같은 폭이 이어지는 만큼 세로로
      grow: while (z1 + 1 < G) {
        for (let x = gx; x <= x1; x += 1) {
          const j = (z1 + 1) * G + x;
          if (!occ[j] || used[j]) break grow;
        }
        z1 += 1;
      }
      for (let z = gz; z <= z1; z += 1) for (let x = gx; x <= x1; x += 1) used[z * G + x] = 1;
      // ⚠ 끝은 **포함**이다(마지막 1m). 배타로 두면 여유 0 에서도 `floor(x1/colM)` 이
      //   다음 컬럼을 물어 폐기 범위가 축마다 한 칸씩 는다.
      out.push({ x0: ox + gx * B, z0: oz + gz * B,
                 x1: ox + (x1 + 1) * B - 1, z1: oz + (z1 + 1) * B - 1 });
      if (out.length > ROAD_RECT_MAX) return null;    // 너무 잘게 흩어졌다 — 통째가 싸다
    }
  }
  return out;
};

// 앵커에서 먼 것부터 버린다 — 「개수 상한을 넘으면 버린다」가 아니라 **거리**로 자른다
// (roadCells 가 2026-08-22 에 개수 상한으로 재메시를 되살린 자리와 같은 함정).
const evict = () => {
  if (_tiles.size <= TILE_MAX || !_anchor) return;
  const cand = [..._tiles.keys()].map((k) => {
    const [a, b] = k.split('_').map(Number);
    return [k, Math.max(Math.abs(a - _anchor[0]), Math.abs(b - _anchor[1]))];
  }).sort((p, q) => q[1] - p[1]);
  for (let i = 0; i < cand.length && _tiles.size > TILE_MAX; i += 1) {
    if (cand[i][1] === 0) break;                    // 발밑 타일은 안 버린다
    _tiles.delete(cand[i][0]);
    _gen += 1;
  }
  noteMem();
};

/** 카메라가 선 타일을 알려준다 — 그 둘레 한 칸(3×3)만 받는다.
 *  ⚠ 반경을 키우면 상주 MB 가 제곱으로 는다. 안 받은 타일은 폴리곤으로 떨어질 뿐이라
 *    **넓게 받을 이유가 없다** — 느려지는 건 아직 안 온 자리뿐이다. */
export const setRoadBakeAnchor = (tx, tz) => {
  if (!ON) return;
  loadManifest();
  if (_anchor && _anchor[0] === tx && _anchor[1] === tz) return;
  _anchor = [tx, tz];
  if (_ok !== true || !_have) return;               // 매니페스트가 오면 위에서 다시 부른다
  for (let dz = -1; dz <= 1; dz += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      const k = tkey(tx + dx, tz + dz);
      if (_tiles.has(k) || !_have.has(k)) continue; // 이미 있거나, 애초에 안 구운 타일
      _tiles.set(k, 'loading');
      _gen += 1;
      _queue.push([tx + dx, tz + dz]);
    }
  }
  // 가까운 것부터 — 대역이 좁아도 발밑이 먼저 찬다
  _queue.sort((p, q) => (Math.max(Math.abs(p[0] - tx), Math.abs(p[1] - tz))
    - Math.max(Math.abs(q[0] - tx), Math.abs(q[1] - tz))));
  evict();
  pump();
};

// ── 조회 ─────────────────────────────────────────────────────────────────────
/** ★ 이 «정수» 월드 좌표의 도로 등급. 답할 수 없으면 **-1**(호출자가 폴리곤으로 떨어진다).
 *  @returns {number} -1 | 0 없음 | 1 간선 | 2 차도 | 3 보행로 */
export const roadBakeKindAt = (wx, wz) => {
  if (!ON || _ok !== true) return -1;
  // 정수 격자 밖은 안 답한다(머리주석 ★). `| 0` 으로 자르지 않는 이유 — 자르면 «틀린 답»이 된다.
  if (!Number.isInteger(wx) || !Number.isInteger(wz)) return -1;
  const dxAbs = wx - OX, dzAbs = wz - OZ;
  const tx = Math.floor(dxAbs / TILE_M), tz = Math.floor(dzAbs / TILE_M);
  let buf = _lbuf;
  if (tx !== _lx || tz !== _lz || _lgen !== _gen) { // 타일·세대가 바뀔 때만 문자열을 만든다(위 §)
    const v = _tiles.get(tkey(tx, tz));
    buf = (typeof v === 'string' || !v) ? null : v;
    _lx = tx; _lz = tz; _lbuf = buf; _lgen = _gen;
  }
  if (!buf) return -1;
  return buf[(dzAbs - tz * TILE_M) * N + (dxAbs - tx * TILE_M)];
};

/** 창(窓) 하나를 구운 타일에서 «통째로» 채운다 — `roadCells.roadMaskFill` 의 빠른 길.
 *
 *  ⚠ **걸치는 타일이 하나라도 안 왔으면 아무것도 안 쓰고 물러난다(-1).** 반은 구운 값,
 *    반은 폴리곤으로 채우면 링이 아직 안 온 자리에서 두 답이 갈려 **창 안에 이음매**가
 *    생긴다 — 그 이음매가 곧 「칠한 자리와 깎은 자리가 어긋나 길 옆에 포장 턱이 남는」
 *    증상이다(roadCells.roadMaskFill 머리주석 ⚠).
 *
 *  @param {Uint8Array} out 길이 W*W
 *  @returns {number} -2 굽기가 깨졌다(엄격 모드 — 호출자는 계산하지 말고 «도로 없음»으로) ·
 *    -1 아직 안 왔다(호출자가 종전 경로로) · 0 채웠고 도로 없음 · 1 채웠고 도로 있음 */
export const roadBakeFill = (x0, z0, W, out) => {
  if (!ON) return -1;
  if (_ok === false) return BAKE_STRICT ? -2 : -1;
  if (_ok !== true) return -1;
  const tx0 = Math.floor((x0 - OX) / TILE_M), tx1 = Math.floor((x0 + W - 1 - OX) / TILE_M);
  const tz0 = Math.floor((z0 - OZ) / TILE_M), tz1 = Math.floor((z0 + W - 1 - OZ) / TILE_M);
  for (let tz = tz0; tz <= tz1; tz += 1) {
    for (let tx = tx0; tx <= tx1; tx += 1) {
      const v = _tiles.get(tkey(tx, tz));
      if (v === 'missing' && BAKE_STRICT) return -2;
      if (!v || typeof v === 'string') return -1;
    }
  }
  let any = false;
  for (let tz = tz0; tz <= tz1; tz += 1) {
    for (let tx = tx0; tx <= tx1; tx += 1) {
      const buf = _tiles.get(tkey(tx, tz));
      const bx = OX + tx * TILE_M, bz = OZ + tz * TILE_M;
      const ax0 = Math.max(x0, bx), ax1 = Math.min(x0 + W - 1, bx + TILE_M - 1);
      const az0 = Math.max(z0, bz), az1 = Math.min(z0 + W - 1, bz + TILE_M - 1);
      for (let wz = az0; wz <= az1; wz += 1) {
        let s = (wz - bz) * N + (ax0 - bx);
        let d = (wz - z0) * W + (ax0 - x0);
        for (let wx = ax0; wx <= ax1; wx += 1, s += 1, d += 1) {
          const v = buf[s];
          out[d] = v;
          if (v) any = true;
        }
      }
    }
  }
  return any ? 1 : 0;
};

// 콘솔 손잡이 — 「지금 구운 도로를 쓰고 있나」를 눈이 아니라 **값**으로 답하게 한다.
// (`__roadBake.stats()` · 워커·노드에는 window 가 없으므로 가드한다 — memBudget 과 같은 규약)
if (typeof window !== 'undefined') {
  window.__roadBake = { stats: roadBakeStats, kindAt: roadBakeKindAt, epoch: roadBakeEpoch };
}

export default {
  roadBakeReady, roadBakeKindAt, roadBakeFill,
  setRoadBakeAnchor, setRoadBakeMeta, roadBakeEpoch, roadBakeStats, onRoadBakeLand,
};
